import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { SellGiftCardDialog } from '../components/Loyalty.tsx';
import { PayTipsDialog } from './Team.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, Keypad, Skeleton, StatusBadge } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { clock, elapsed, money } from '../lib/format.ts';
import { useLiveAction } from '../lib/data.ts';
import { useSession } from '../lib/session.tsx';
import { useNow, useUi } from '../lib/ui-state.tsx';
import { useRegister } from './Pay.tsx';

export interface ShiftReport {
  orders: number;
  guests: number;
  sales_total: number;
  tax_total: number;
  discount_total: number;
  tips: number;
  by_method: Record<string, number>;
  cash_in: number;
  /** Missing on shifts closed before cash in and out existed. */
  paid_in?: number;
  paid_out?: number;
  change_given: number;
  cancelled_orders: number;
  void_lines: number;
  /** Missing on shifts closed before refunds existed. */
  refunds?: number;
  refunds_total?: number;
  /** Sections added by other modules: gift cards sold and cancelled on the shift. */
  extras?: { gift_cards?: GiftCardShift; tips_paid?: { count: number; amount: number } };
}
interface GiftCardShift {
  sold: number;
  sold_total: number;
  sold_cash: number;
  sold_card: number;
  voided: number;
  voided_total: number;
  voided_cash: number;
  voided_card: number;
}
interface Session {
  id: string;
  opened_at: string;
  closed_at: string | null;
  opening_cash: number;
  expected_cash: number | null;
  counted_cash: number | null;
  difference: number | null;
  z_number: number | null;
  report: ShiftReport | null;
  note: string | null;
}

function amountKey(current: string, k: string): string {
  if (k === 'back') return current.slice(0, -1);
  if (k === '00') return current ? `${current}00`.slice(0, 9) : current;
  return `${current}${k}`.replace(/^0+/, '').slice(0, 9);
}

/** What the drawer should hold: the float, cash sales, and cash put in or taken out. */
const expectedCash = (opening: number, r: ShiftReport) => opening + r.cash_in + (r.paid_in ?? 0) - (r.paid_out ?? 0);

export function ReportTable({ report, opening }: { report: ShiftReport; opening: number }) {
  const gift = report.extras?.gift_cards;
  return (
    <dl className="report">
      <dt>{t('shift.r_orders')}</dt>
      <dd>{report.orders}</dd>
      <dt>{t('shift.r_guests')}</dt>
      <dd>{report.guests}</dd>
      <dt className="strong">{t('shift.r_sales')}</dt>
      <dd className="strong">{money(report.sales_total)}</dd>
      <dt className="muted">{t('totals.vat_included')}</dt>
      <dd className="muted">{money(report.tax_total)}</dd>
      {report.refunds ? (
        <>
          <dt>{t('shift.r_refunds', { n: report.refunds })}</dt>
          <dd>{money(-(report.refunds_total ?? 0), { sign: true })}</dd>
        </>
      ) : null}
      <dt>{t('shift.r_discounts')}</dt>
      <dd>{money(report.discount_total)}</dd>
      <dt>{t('shift.r_tips')}</dt>
      <dd>{money(report.tips)}</dd>
      <dt>
        <Icon name="cash" size={14} /> {t('pay.method_cash')}
      </dt>
      <dd>{money(report.by_method.cash ?? 0)}</dd>
      <dt>
        <Icon name="card" size={14} /> {t('pay.method_card')}
      </dt>
      <dd>{money(report.by_method.card ?? 0)}</dd>
      <dt>
        <Icon name="receipt" size={14} /> {t('pay.method_other')}
      </dt>
      <dd>{money(report.by_method.other ?? 0)}</dd>
      {report.by_method.gift_card ? (
        <>
          <dt>
            <Icon name="gift" size={14} /> {t('pay.method_gift_card')}
          </dt>
          <dd>{money(report.by_method.gift_card)}</dd>
        </>
      ) : null}
      {report.by_method.points ? (
        <>
          <dt>
            <Icon name="star" size={14} /> {t('pay.method_points')}
          </dt>
          <dd>{money(report.by_method.points)}</dd>
        </>
      ) : null}
      {gift ? (
        <>
          <dt className="report-section">
            <Icon name="gift" size={14} /> {tn('shift.r_gift_sold', gift.sold)}
          </dt>
          <dd className="report-section">{money(gift.sold_total)}</dd>
          <dt className="muted report-indent">{t('shift.r_gift_split', { cash: money(gift.sold_cash), card: money(gift.sold_card) })}</dt>
          <dd />
          {gift.voided ? (
            <>
              <dt>{tn('shift.r_gift_voided', gift.voided)}</dt>
              <dd>{money(-gift.voided_total, { sign: true })}</dd>
            </>
          ) : null}
          {gift.sold_card || gift.voided_card ? (
            <>
              <dt>{t('shift.r_card_terminal')}</dt>
              <dd>{money((report.by_method.card ?? 0) + gift.sold_card - gift.voided_card)}</dd>
            </>
          ) : null}
        </>
      ) : null}
      <dt>{t('shift.r_opening')}</dt>
      <dd>{money(opening)}</dd>
      {report.paid_in ? (
        <>
          <dt>{gift?.sold_cash ? t('shift.r_paid_in_gift') : t('shift.r_paid_in')}</dt>
          <dd>{money(report.paid_in, { sign: true })}</dd>
        </>
      ) : null}
      {report.paid_out ? (
        <>
          <dt>{gift?.voided_cash ? t('shift.r_paid_out_gift') : t('shift.r_paid_out')}</dt>
          <dd>{money(-report.paid_out, { sign: true })}</dd>
        </>
      ) : null}
      {report.extras?.tips_paid ? (
        <>
          <dt className="muted report-indent">{tn('shift.r_tips_paid', report.extras.tips_paid.count)}</dt>
          <dd className="muted">{money(-report.extras.tips_paid.amount, { sign: true })}</dd>
        </>
      ) : null}
      <dt className="strong">{t('shift.r_expected_cash')}</dt>
      <dd className="strong">{money(expectedCash(opening, report))}</dd>
      <dt>{t('shift.r_cancelled')}</dt>
      <dd>{report.cancelled_orders}</dd>
      <dt>{t('shift.r_voids')}</dt>
      <dd>{report.void_lines}</dd>
    </dl>
  );
}

export function Shift() {
  const { can } = useSession();
  const { fail, toast } = useUi();
  const now = useNow();
  const { registers, register, choose, reload: reloadRegisters } = useRegister();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);
  const [cash, setCash] = useState(false);
  const [selling, setSelling] = useState(false);
  const [payingTips, setPayingTips] = useState(false);
  const [openingDrawer, setOpeningDrawer] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [closed, setClosed] = useState<Session | null>(null);
  const { data: current, reload } = useLiveAction<{ session: Session | null; report: ShiftReport | null }>(
    'pos.current_shift',
    { register: register?.id ?? '00000000-0000-4000-8000-000000000000' },
    register ? ['pos', 'sales'] : [],
  );
  useEffect(() => setTyped(''), [register?.id]);
  // A cash drawer on the register's printer can be opened from here, with a reason.
  useEffect(() => {
    setDrawer(false);
    if (!register?.printer || !can('printing.drawer')) return;
    action<{ drawer: boolean } | null>('printing.register_printer', { register: register.id }).then((p) => setDrawer(!!p?.drawer), () => setDrawer(false));
  }, [register?.id, register?.printer]);

  const open = async () => {
    if (!register) return;
    setBusy(true);
    try {
      await action('pos.open_shift', { register: register.id, opening_cash: Number(typed || 0) * 100 });
      toast(t('shift.opened_toast', { name: register.name }));
      setTyped('');
      await Promise.all([reload(), reloadRegisters()]);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  if (!registers) {
    return (
      <Shell>
        <div className="page narrow">
          <Skeleton h={360} r="var(--radius-lg)" />
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="page narrow shift-page">
        <div className="page-head">
          <div>
            <h1>{t('shift.title')}</h1>
            <p className="muted">{t('shift.subtitle')}</p>
          </div>
        </div>

        {registers.length > 1 ? (
          <div className="toolbar">
            <div className="segmented" role="tablist" aria-label={t('shift.register')}>
              {registers.map((r) => (
                <button key={r.id} role="tab" aria-selected={register?.id === r.id} className={register?.id === r.id ? 'active' : ''} onClick={() => choose(r.id)}>
                  {r.name}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {!register ? (
          <EmptyState icon="register" title={registers.length ? t('shift.pick_register') : t('pay.no_registers')} />
        ) : !current ? (
          <Skeleton h={360} r="var(--radius-lg)" />
        ) : current.session && current.report ? (
          <section className="card">
            <header className="card-head">
              <div>
                <h2>{register.name}</h2>
                <span className="muted">
                  {t('shift.opened_at', { time: clock(current.session.opened_at), ago: elapsed(current.session.opened_at, now) })}
                </span>
              </div>
              <StatusBadge tone="ok" icon="checkCircle" label={t('shift.open_state')} />
            </header>
            <ReportTable report={current.report} opening={current.session.opening_cash} />
            {can('pos.shift') ? (
              <div className="shift-actions">
                <Button size="xl" icon="cash" onClick={() => setCash(true)}>
                  {t('shift.cash_moves')}
                </Button>
                {can('loyalty.sell') && can('pos.pay') ? (
                  <Button size="xl" icon="gift" onClick={() => setSelling(true)}>
                    {t('gift.sell')}
                  </Button>
                ) : null}
                {can('team.tips') ? (
                  <Button size="xl" icon="users" onClick={() => setPayingTips(true)}>
                    {t('team.pay_tips')}
                  </Button>
                ) : null}
                {drawer ? (
                  <Button size="xl" icon="box" onClick={() => setOpeningDrawer(true)}>
                    {t('printing.open_drawer')}
                  </Button>
                ) : null}
                <Button variant="danger" size="xl" icon="register" onClick={() => setClosing(true)}>
                  {t('shift.close')}
                </Button>
              </div>
            ) : null}
          </section>
        ) : (
          <section className="card">
            <header className="card-head">
              <div>
                <h2>{register.name}</h2>
                <span className="muted">{t('shift.open_hint')}</span>
              </div>
              <StatusBadge tone="neutral" icon="pause" label={t('shift.closed_state')} />
            </header>
            {can('pos.shift') ? (
              <>
                <div className="amount-display">
                  <span className="muted">{t('shift.opening_cash')}</span>
                  <strong>{money(Number(typed || 0) * 100)}</strong>
                </div>
                <Keypad onKey={(k) => setTyped((x) => amountKey(x, k))} extra={{ key: '00', label: '00' }} />
                <Button variant="primary" size="xl" icon="check" className="w-full" busy={busy} onClick={open}>
                  {t('shift.open')}
                </Button>
              </>
            ) : (
              <p className="callout callout-warn">
                <Icon name="alert" size={16} /> {t('pay.no_shift_ask')}
              </p>
            )}
          </section>
        )}
      </div>

      {openingDrawer && register ? <DrawerDialog register={register.id} onClose={() => setOpeningDrawer(false)} /> : null}
      {selling && current?.session ? <SellGiftCardDialog session={current.session.id} onClose={() => setSelling(false)} onSold={() => void reload()} /> : null}
      {payingTips && current?.session ? (
        <PayTipsDialog
          person={null}
          session={current.session}
          drawer={current.report ? expectedCash(current.session.opening_cash, current.report) : undefined}
          onClose={() => setPayingTips(false)}
          onPaid={() => {
            setPayingTips(false);
            void reload();
          }}
        />
      ) : null}
      {cash && current?.session ? <CashDialog session={current.session} onClose={() => setCash(false)} onDone={reload} /> : null}
      {closing && current?.session && current.report ? (
        <CloseShift
          session={current.session}
          report={current.report}
          onClose={() => setClosing(false)}
          onClosed={(s) => {
            setClosing(false);
            setClosed(s);
            void reload();
            void reloadRegisters();
          }}
        />
      ) : null}
      {closed && closed.report ? (
        <Dialog title={t('shift.z_title', { n: closed.z_number ?? '' })} onClose={() => setClosed(null)}>
          <ReportTable report={closed.report} opening={closed.opening_cash} />
          <dl className="report">
            <dt>{t('shift.counted')}</dt>
            <dd>{money(closed.counted_cash ?? 0)}</dd>
            <dt className="strong">{t('shift.difference')}</dt>
            <dd className={`strong ${closed.difference ? 'text-danger' : 'text-ok'}`}>{money(closed.difference ?? 0, { sign: true })}</dd>
          </dl>
          {closed.note ? <p className="muted">{closed.note}</p> : null}
          <Button variant="secondary" icon="receipt" className="w-full" onClick={() => window.print()}>
            {t('shift.print')}
          </Button>
        </Dialog>
      ) : null}
    </Shell>
  );
}

function CloseShift({ session, report, onClose, onClosed }: { session: Session; report: ShiftReport; onClose: () => void; onClosed: (s: Session) => void }) {
  const { fail, toast } = useUi();
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const expected = expectedCash(session.opening_cash, report);
  const counted = typed ? Number(typed) * 100 : null;
  const diff = counted === null ? null : counted - expected;

  const close = async () => {
    if (counted === null) return;
    setBusy(true);
    try {
      const s = await action<Session>('pos.close_shift', { session: session.id, counted_cash: counted, ...(note.trim() ? { note: note.trim() } : {}) });
      toast(t('shift.closed_toast', { n: s.z_number ?? '' }));
      onClosed(s);
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={t('shift.close')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" icon="register" busy={busy} onClick={close} disabled={counted === null || (diff !== 0 && note.trim().length < 3)}>
            {t('shift.close_submit')}
          </Button>
        </>
      }
    >
      <p className="muted">{t('shift.count_hint', { amount: money(expected) })}</p>
      <div className="amount-display">
        <span className="muted">{t('shift.counted')}</span>
        <strong>{counted !== null ? money(counted) : '–'}</strong>
        {diff !== null ? (
          diff === 0 ? (
            <StatusBadge tone="ok" icon="check" label={t('shift.matches')} />
          ) : (
            <StatusBadge
              tone="danger"
              icon="alert"
              label={diff > 0 ? t('shift.over', { amount: money(diff) }) : t('shift.short', { amount: money(-diff) })}
            />
          )
        ) : null}
      </div>
      <Keypad onKey={(k) => setTyped((x) => amountKey(x, k))} extra={{ key: '00', label: '00' }} />
      {diff !== null && diff !== 0 ? (
        <Field label={t('shift.note')} hint={t('shift.note_hint')}>
          <input className="input" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        </Field>
      ) : null}
    </Dialog>
  );
}

interface CashMoveRow {
  id: string;
  kind: 'in' | 'out';
  amount: number;
  reason: string;
  source: string | null;
  at: string;
  by_name: string | null;
}
const CASH_REASONS = { in: ['shift.reason_change', 'shift.reason_float'], out: ['shift.reason_supplier', 'shift.reason_bank', 'shift.reason_expense'] } as const;

/** Cash put into or taken out of the drawer during the shift, always with a reason. */
function CashDialog({ session, onClose, onDone }: { session: Session; onClose: () => void; onDone: () => Promise<void> }) {
  const { fail, toast } = useUi();
  const [kind, setKind] = useState<'in' | 'out'>('out');
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [moves, setMoves] = useState<CashMoveRow[] | null>(null);
  const load = () => action<CashMoveRow[]>('pos.cash_moves', { session: session.id }).then(setMoves, fail);
  useEffect(() => void load(), []);
  const amount = Number(typed || 0) * 100;
  const save = async () => {
    setBusy(true);
    try {
      await action('pos.cash_move', { session: session.id, kind, amount, reason: reason.trim() });
      toast(t(kind === 'in' ? 'shift.cash_in_toast' : 'shift.cash_out_toast', { amount: money(amount) }));
      setTyped('');
      setReason('');
      await Promise.all([load(), onDone()]);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('shift.cash_moves')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save} disabled={!amount || reason.trim().length < 3}>
            {t(kind === 'in' ? 'shift.cash_in_save' : 'shift.cash_out_save', { amount: money(amount) })}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="segmented" role="radiogroup" aria-label={t('shift.cash_kind')}>
          {(['out', 'in'] as const).map((k) => (
            <button key={k} role="radio" aria-checked={kind === k} className={kind === k ? 'active' : ''} onClick={() => setKind(k)}>
              <Icon name={k === 'in' ? 'plus' : 'minus'} size={16} /> {t(k === 'in' ? 'shift.cash_in' : 'shift.cash_out')}
            </button>
          ))}
        </div>
        <div className="amount-display">
          <span className="muted">{t('shift.amount')}</span>
          <strong>{money(amount)}</strong>
        </div>
        <Keypad onKey={(k) => setTyped((x) => amountKey(x, k))} extra={{ key: '00', label: '00' }} />
        <Field label={t('shift.reason')}>
          <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="chips">
          {CASH_REASONS[kind].map((r) => (
            <button key={r} type="button" className={`chip${reason === t(r) ? ' active' : ''}`} onClick={() => setReason(t(r))}>
              {t(r)}
            </button>
          ))}
        </div>
        {moves && moves.length ? (
          <section className="list-card" aria-label={t('shift.cash_list')}>
            {moves.map((m) => (
              <div key={m.id} className="list-row">
                <span className={`list-icon move-${m.kind === 'in' ? 'purchase' : 'waste'}`} aria-hidden="true">
                  <Icon name={m.kind === 'in' ? 'plus' : 'minus'} size={18} />
                </span>
                <span className="list-main">
                  <span className="list-title">{m.reason}</span>
                  <span className="list-sub">{[clock(m.at), m.by_name, m.source].filter(Boolean).join(' · ')}</span>
                </span>
                <strong className={m.kind === 'in' ? 'text-ok' : ''}>{money(m.kind === 'in' ? m.amount : -m.amount, { sign: true })}</strong>
              </div>
            ))}
          </section>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Opens the cash drawer without a sale. Who and why are kept with the print log. */
function DrawerDialog({ register, onClose }: { register: string; onClose: () => void }) {
  const { fail, toast } = useUi();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await action('printing.open_drawer', { register, reason: reason.trim() });
      toast(t('printing.drawer_opened'));
      onClose();
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('printing.open_drawer_title')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="box" busy={busy} disabled={reason.trim().length < 3} onClick={() => void go()}>
            {t('printing.open_drawer')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (reason.trim().length >= 3) void go();
        }}
      >
        <p className="muted">{t('printing.open_drawer_body')}</p>
        <Field label={t('printing.drawer_reason')}>
          <input className="input" value={reason} maxLength={200} placeholder={t('printing.drawer_reason_placeholder')} onChange={(e) => setReason(e.target.value)} data-autofocus />
        </Field>
      </form>
    </Dialog>
  );
}
